import type { UserProfileI } from 'declarations/interfaces/user';

declare global {
  interface Window {
    pendo?: {
      initialize: (options: PendoInitializeOptions) => void;
      isReady: () => boolean;
    };
  }
}

interface PendoVisitor {
  id: string;
}

interface PendoAccount {
  id: string;
  app?: string;
  app_id?: string;
}

interface PendoInitializeOptions {
  visitor: PendoVisitor;
  account: PendoAccount;
}

/** Remove Pendo image badge if present (Pendo may inject duplicate elements) */
const removePendoImageBadge = (): void => {
  try {
    const element = document.getElementById('pendo-image-badge-c05e548c');
    if (element) element.remove();
  } catch {
    /* no-op */
  }
};

/**
 * Reposition the Pendo resource center badge to the main header anchor.
 * Matches WarrQueue behavior: badge appears between Avatar and theme toggle.
 */
const repositionResourceCenterBadge = (): void => {
  try {
    removePendoImageBadge();

    const badge = document.querySelector(
      '._pendo-resource-center-badge-container'
    ) as HTMLButtonElement | null;
    if (!badge) return;

    const size = 32;
    badge.style.width = `${size}px`;
    badge.style.height = `${size}px`;
    badge.style.borderRadius = '50%';
    badge.style.boxShadow = 'rgba(0, 0, 0, 0.15) 2px 0px 6px 0px';
    const img = badge.querySelector(
      'img._pendo-resource-center-badge-image'
    ) as HTMLImageElement | null;
    if (img) {
      img.style.width = `${size}px`;
      img.style.height = `${size}px`;
      img.style.boxShadow = 'none';
    }

    const anchor = document.getElementById('pendo-badge-anchor');
    if (anchor) {
      const rect = anchor.getBoundingClientRect();
      const top = Math.max(0, rect.top - 1);
      const right = Math.max(0, window.innerWidth - rect.right - 11);

      badge.style.position = 'fixed';
      badge.style.top = `${top}px`;
      badge.style.left = 'auto';
      badge.style.right = `${right}px`;
      badge.style.bottom = 'auto';
      badge.style.inset = `${top}px ${right}px auto auto`;
      badge.style.zIndex = '1200';
      return;
    }

    badge.style.position = 'fixed';
    badge.style.top = 'auto';
    badge.style.left = 'auto';
    badge.style.right = '24px';
    badge.style.bottom = '24px';
    badge.style.inset = 'auto 24px 24px auto';
    badge.style.zIndex = '1200';
  } catch {
    /* no-op */
  }
};

/**
 * Start observing DOM to reposition Pendo badge when it appears and on layout changes.
 * Pendo injects the badge asynchronously. Matches WarrQueue: badge in main header between Avatar and theme toggle.
 */
const setupBadgeRepositioning = (): void => {
  repositionResourceCenterBadge();

  const reposition = (): void => {
    removePendoImageBadge();
    repositionResourceCenterBadge();
  };

  const observer = new MutationObserver(reposition);
  observer.observe(document.body, { childList: true, subtree: true });

  const pollInterval = setInterval(reposition, 500);
  setTimeout(() => clearInterval(pollInterval), 10000);
};

/**
 * Initialize Pendo with basic user data for resource center
 */
export const initializePendo = (userProfile: UserProfileI): void => {
  if (typeof window === 'undefined' || !window.pendo) {
    return;
  }

  try {
    window.pendo.initialize({
      visitor: {
        id: userProfile.userId,
      },
      account: {
        id: 'warrchirp',
        app: 'WarrChirp',
        app_id: 'warrchirp',
      },
    });

    removePendoImageBadge();
    setupBadgeRepositioning();
  } catch (error) {
    // Failed to initialize Pendo
  }
};
